import { useCallback, useEffect, useRef } from "react";
import { programSlices } from "../services/programSlices";

/** 视口预取的 rootMargin:提前约一屏入队,正常滚速不撞加载边界(契约 §3.1) */
const PREFETCH_ROOT_MARGIN = "100% 0px";

/**
 * 占位区块的视口预取:单个共享 IntersectionObserver,同一次回调里按 program
 * 归并 id,凑批后再通知编排器(契约 §5.2 第 3 步)。
 *
 * root 缺省 = 浏览器视口(Program 页);Home 传左列滚动容器。
 * resetDeps:root 元素可能随这些值重建(如 Home 的 guest/登录视图切换),
 * 变化时重建 observer 并重新 observe 已注册的占位元素。
 *
 * 返回 registerPlaceholder,占位区块经 onRegister 挂入,programId/requirementId
 * 由根元素 data 属性携带。
 */
export function useSlicePrefetch(
    rootRef?: React.RefObject<HTMLElement | null>,
    resetDeps: unknown[] = [],
) {
    const targets = useRef(new Map<Element, { programId: string; requirementId: string }>());
    const observerRef = useRef<IntersectionObserver | null>(null);

    const registerPlaceholder = useCallback((el: HTMLElement) => {
        targets.current.set(el, {
            programId: el.dataset.programId!,
            requirementId: el.dataset.requirementId!,
        });
        observerRef.current?.observe(el);
        return () => {
            targets.current.delete(el);
            observerRef.current?.unobserve(el);
        };
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(entries => {
            const byProgram = new Map<string, string[]>();
            entries.forEach(entry => {
                if (!entry.isIntersecting) return;
                const meta = targets.current.get(entry.target);
                if (!meta) return;
                const ids = byProgram.get(meta.programId) ?? [];
                ids.push(meta.requirementId);
                byProgram.set(meta.programId, ids);
            });
            byProgram.forEach((ids, programId) => programSlices.prioritize(programId, ids));
        }, { root: rootRef?.current ?? null, rootMargin: PREFETCH_ROOT_MARGIN });
        observerRef.current = observer;
        targets.current.forEach((_, el) => observer.observe(el));
        return () => {
            observer.disconnect();
            observerRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, resetDeps);

    return registerPlaceholder;
}
